import { z } from 'zod';
import { errorCodes } from '../errors';
import { daySchema } from './common';

export const setLogInput = z.object({
  clientIdempotencyKey: z.string().uuid(),
  exerciseId: z.string().uuid(),
  workoutExerciseId: z.string().uuid().nullable().optional(),
  setIndex: z.number().int().min(0),
  reps: z.number().int().min(0).max(1000).nullable().optional(),
  weightKg: z.number().min(0).max(1000).nullable().optional(),
  durationSeconds: z.number().int().min(0).nullable().optional(),
  distanceM: z.number().min(0).nullable().optional(),
  completedAt: z.string().datetime(),
});

export const workoutLogInput = z.object({
  clientIdempotencyKey: z.string().uuid(),
  workoutId: z.string().uuid().nullable().optional(),
  assignmentId: z.string().uuid().nullable().optional(),
  titleOverride: z.string().max(160).nullable().optional(),
  startedAt: z.string().datetime(),
  completedAt: z.string().datetime().nullable().optional(),
  sets: z.array(setLogInput).max(500).default([]),
  feedback: z
    .object({
      difficulty: z.number().int().min(1).max(5).optional(),
      energy: z.number().int().min(1).max(5).optional(),
      soreness: z.number().int().min(1).max(5).optional(),
      pain: z.boolean().optional(),
      painNote: z.string().max(500).optional(),
      comment: z.string().max(2000).optional(),
    })
    .optional(),
});

/** Batch sinkronizacija iz mobilne aplikacije nakon offline rada. */
export const syncWorkoutsInput = z.object({ logs: z.array(workoutLogInput).max(50) });

export const personalRecordOutput = z.object({
  exerciseId: z.string(),
  exerciseName: z.string(),
  metric: z.string(),
  value: z.number(),
});

/**
 * Ikona postignuca je IME, ne znak.
 *
 * Prije je server slao emoji (`🏆`), pa je izgled sustava ovisio o tome kako ga
 * crta tudi font — na svakom uredaju drukcije, i uvijek strano ostatku suceja.
 * Sada server bira znacenje, a klijent crta vlastitu SVG ikonu u istom potezu
 * kao sve ostale. `generic` je odusak za kljuc bez definicije.
 */
export const achievementIcon = z.enum(['workout', 'streak', 'record', 'goal', 'generic']);

/**
 * Postignuće otključano baš tim treningom.
 *
 * Naziv i ikonu bira server (`achievementDef` u API-ju) — pragovi se mijenjaju
 * bez nove verzije aplikacije, pa klijent ne drži drugi popis istih naziva.
 */
export const unlockedAchievementOutput = z.object({
  key: z.string(),
  label: z.string(),
  icon: achievementIcon,
});

export const workoutCompletedOutput = z.object({
  workoutLogId: z.string(),
  durationSeconds: z.number().int(),
  exerciseCount: z.number().int(),
  totalVolumeKg: z.number(),
  newPersonalRecords: z.array(personalRecordOutput),
  /** Samo NOVOOTKLJUČANA postignuća — server ih šalje jednom, pa se čestitka ne ponavlja. */
  achievements: z.array(unlockedAchievementOutput),
});

/** Jedan trening iz batcha: id zapisa na serveru i postignuća koja je otključao. */
export const syncedWorkoutOutput = z.object({
  clientIdempotencyKey: z.string(),
  workoutLogId: z.string(),
  achievements: z.array(unlockedAchievementOutput),
});

/**
 * Odgovor batch sinkronizacije. `results` postoji jer se postignuća (osobito
 * `pr_*`) računaju samo u trenutku upisa — bez njih bi rekord otključan iz
 * batcha bio trajno izgubljen za sučelje.
 */
export const syncWorkoutsOutput = z.object({
  accepted: z.array(z.string()),
  rejected: z.array(z.object({ key: z.string(), reason: z.enum(errorCodes) })),
  results: z.array(syncedWorkoutOutput),
});

export const measurementInput = z.object({
  clientIdempotencyKey: z.string().uuid().optional(),
  measuredOn: daySchema,
  weightKg: z.number().min(20).max(400).nullable().optional(),
  waistCm: z.number().min(30).max(250).nullable().optional(),
  chestCm: z.number().min(30).max(250).nullable().optional(),
  hipsCm: z.number().min(30).max(250).nullable().optional(),
  armCm: z.number().min(10).max(100).nullable().optional(),
  thighCm: z.number().min(20).max(150).nullable().optional(),
  bodyFatPct: z.number().min(1).max(70).nullable().optional(),
  note: z.string().max(1000).nullable().optional(),
  custom: z.record(z.string(), z.number()).optional(),
});

export type WorkoutLogInput = z.infer<typeof workoutLogInput>;
export type AchievementIcon = z.infer<typeof achievementIcon>;
export type SetLogInput = z.infer<typeof setLogInput>;
export type MeasurementInput = z.infer<typeof measurementInput>;
export type UnlockedAchievement = z.infer<typeof unlockedAchievementOutput>;
export type WorkoutCompletedOutput = z.infer<typeof workoutCompletedOutput>;
export type SyncedWorkoutOutput = z.infer<typeof syncedWorkoutOutput>;
export type SyncWorkoutsOutput = z.infer<typeof syncWorkoutsOutput>;
